/**
 * voko-server 网页接口的唯一调用点。登录凭据存在 localStorage：换标签页、重开浏览器都不用重新登录。
 * access token 只有 15 分钟；过期时用 refresh token 换一对新的再重试，直到 refresh token 也过期（服务端定的登录有效期）才回登录页。
 */

import { reactive } from 'vue'

import { parseFrames } from './chat.js'
import { apiJson, invalidApiResponse } from './api-response.js'

const TOKEN_KEY = 'voko.access_token'
const REFRESH_KEY = 'voko.refresh_token'

const GUILD_KEY = 'voko.current_guild'

/**
 * 登录状态与这个人的账号：外壳（顶栏、侧栏）和各页面共用。`user` 是 /me 的 user，`agents` 是他不在回收站里的 Agent（卡片形状）。
 * 没登录或还没取回来时 user 为 null、agents 为空。
 * @type {{ loggedIn: boolean, user: { id: string, email: string, role: string, handle: string }|null, agents: Array<{ id: string, name: string, full_name: string, status: string, guilds: Array<{ slug: string, name: string, role: 'company'|'member' }> }> }}
 */
export const session = reactive({ loggedIn: localStorage.getItem(TOKEN_KEY) !== null, user: null, agents: [] })

/**
 * 顶栏的「当前公会」：看板、公会城、任务树、排行榜都看这个公会的。`options` 是这个人能选的公会（名下 Agent 在册的、他负责的；运营是全部），
 * `slug` 是选中的那个，没有可选的公会时为 null；`loaded` 是可选范围取回来了没有。选择记在本浏览器里，下次打开还是它。
 * @type {{ options: Array<{ id: string, name: string, slug: string }>, slug: string|null, loaded: boolean }}
 */
export const currentGuild = reactive({ options: [], slug: null, loaded: false })

/**
 * 登录后取账号、名下 Agent 和可选的公会；上次选的公会还在范围里就沿用，否则选第一个。
 * 页面上建 / 删 Agent、加入 / 退出公会之后再调一次，外壳跟着更新。
 */
export async function loadAccount() {
  const [me, mine, { guilds }] = await Promise.all([api('GET', '/api/voko/me'), api('GET', '/api/voko/agents'), api('GET', '/api/voko/me/guilds')])
  session.user = me.user
  session.agents = mine.agents
  const saved = localStorage.getItem(GUILD_KEY)
  currentGuild.options = guilds
  currentGuild.loaded = true
  selectGuild(guilds.some((guild) => guild.slug === saved) ? saved : guilds.length === 0 ? null : guilds[0].slug)
}

/**
 * 我名下在某个公会在册的 Agent：公司 Agent 在前（发包、管理用它），其余按名字。
 * @param {string} slug
 */
export function agentsIn(slug) {
  return session.agents
    .filter((agent) => agent.guilds.some((guild) => guild.slug === slug))
    .sort((a, b) => Number(b.guilds.some((guild) => guild.slug === slug && guild.role === 'company')) - Number(a.guilds.some((guild) => guild.slug === slug && guild.role === 'company')) || a.name.localeCompare(b.name))
}

/**
 * 以某个 Agent 身份调的接口都要带当前公会（?guild=slug）。只在外壳确认选了公会之后的页面里用（路由 meta.guild）。
 * @param {string} path 可以已经带查询参数
 */
export function inGuild(path) {
  const url = new URL(path, window.location.origin)
  url.searchParams.set('guild', currentGuild.slug)
  return `${url.pathname}${url.search}`
}

/** @param {string|null} slug */
export function selectGuild(slug) {
  currentGuild.slug = slug
  if (slug === null) localStorage.removeItem(GUILD_KEY)
  else localStorage.setItem(GUILD_KEY, slug)
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY)
}

/**
 * 记下登录结果；传 null 表示退出登录。
 * @param {{ access_token: string, refresh_token: string }|null} next
 */
export function setSession(next) {
  if (next === null) {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
    session.user = null
    session.agents = []
    currentGuild.options = []
    currentGuild.loaded = false
    selectGuild(null)
  } else {
    localStorage.setItem(TOKEN_KEY, next.access_token)
    localStorage.setItem(REFRESH_KEY, next.refresh_token)
  }
  session.loggedIn = next !== null
}

/**
 * 用 refresh token 换一对新的凭据。成功（或别的标签页已经换好了）返回 true；refresh token 无效或过期返回 false。
 * refresh token 只能用一次，重复提交会让服务端判定凭据泄露并撤销整个登录，所以用 Web Locks 让同一浏览器的所有标签页排队：
 * 拿到锁之后先看凭据是不是已经被别人换过了，换过就直接用新的。
 * @param {string} staleToken 刚才被拒绝的那个 access token
 */
function renew(staleToken) {
  return navigator.locks.request('voko-session-refresh', async () => {
    if (getToken() !== staleToken) return getToken() !== null
    const refresh_token = localStorage.getItem(REFRESH_KEY)
    if (refresh_token === null) return false
    const response = await fetch('/api/voko/auth/refresh', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ refresh_token }) })
    if (!response.ok) return false
    setSession(await response.json())
    return true
  })
}

/**
 * 带登录凭据发请求；凭据过期就续期后重试一次，续不上就退出登录并回登录页（登录后回到当前页，否则页面只会空白）。
 * @param {string} url
 * @param {RequestInit & { headers?: Record<string, string> }} init
 */
async function authedFetch(url, init) {
  const send = (token) => fetch(url, { ...init, headers: { ...init.headers, ...(token === null ? {} : { authorization: `Bearer ${token}` }) } })
  const token = getToken()
  const response = await send(token)
  // 登录、注册、重设密码的 401 是「密码错」这类业务结果，不是凭据过期。
  if (response.status !== 401 || token === null || url.startsWith('/api/voko/auth/')) return response
  if (await renew(token)) return send(getToken())
  setSession(null)
  window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname)}`)
  return response
}

/**
 * @param {string} method
 * @param {string} path
 * @param {unknown} [body]
 * @param {string} [idempotencyKey] 写接口的幂等键：同一次提交重试不会重复扣积分
 */
/**
 * 服务端返回的错误：带上它的错误码，页面据此区分「没有这个东西」之类的预期情况。
 * @param {{ error: { message: string, code: string, details?: Record<string, unknown> } }} json
 */
function apiError(json) {
  const error = new Error(json.error.message)
  error.code = json.error.code
  error.details = json.error.details
  return error
}

export async function api(method, path, body, idempotencyKey) {
  const response = await authedFetch(path, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(idempotencyKey === undefined ? {} : { 'idempotency-key': idempotencyKey }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const json = await apiJson(response)
  if (!response.ok) throw apiError(json)
  return json
}

/**
 * 上传一个文件的原始字节（任务附件）。
 * @param {string} path 可以已经带查询参数（如 inGuild 加的公会）
 * @param {File} file
 * @param {AbortSignal} [signal]
 */
export async function upload(path, file, signal) {
  const url = new URL(path, window.location.origin)
  url.searchParams.set('filename', file.name)
  const response = await authedFetch(`${url.pathname}${url.search}`, { method: 'POST', headers: { 'content-type': 'application/octet-stream' }, body: file, signal })
  const json = await apiJson(response)
  if (!response.ok) throw apiError(json)
  return json
}

/**
 * 带登录态下载一个文件并交给浏览器保存（交付文件需要鉴权，不能直接用链接）。
 * @param {string} path
 * @param {string} filename
 */
export async function download(path, filename) {
  const response = await authedFetch(path, {})
  if (!response.ok) throw new Error((await apiJson(response)).error.message)
  const url = URL.createObjectURL(await response.blob())
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * 带登录态读一条 text/event-stream（EventSource 不能带鉴权头，所以用 fetch 读流），每条事件交给 onEvent。
 * 连接结束（服务端关了、网络断了）或 signal 中止时 resolve；打不开（非 200）时抛出带错误码的错误。
 * @param {string} path
 * @param {(event: any) => void} onEvent
 * @param {AbortSignal} signal
 * @param {() => void} onOpen
 */
export async function readStream(path, onEvent, signal, onOpen) {
  let response
  try {
    response = await authedFetch(path, { signal, headers: { accept: 'text/event-stream' } })
  } catch (error) {
    // 网络层的失败（断网、中止）只说明这次没连上：由调用方决定要不要重连。
    if (error.name === 'AbortError' || error.name === 'TypeError') return
    throw error
  }
  if (!response.ok) throw apiError(await apiJson(response))
  if ((response.headers.get('content-type') ?? '').split(';')[0].trim().toLowerCase() !== 'text/event-stream') throw invalidApiResponse(response)
  const reader = response.body.getReader()
  onOpen()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) return
      buffer += decoder.decode(value, { stream: true })
      const parsed = parseFrames(buffer)
      buffer = parsed.rest
      parsed.events.forEach(onEvent)
    }
  } catch (error) {
    if (error.name === 'AbortError' || error.name === 'TypeError') return
    throw error
  }
}
